import React, { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  Copy,
  IndianRupee,
  RefreshCw,
  Undo2,
  Wallet,
} from "lucide-react";
import { toast } from "react-toastify";

import Card from "src/components/common/card/Card";
import Button from "src/components/ui/Button";
import TableV2 from "src/components/ui/TableV2";
import { Badge } from "src/components/ui/Badge";
import paymentApis from "src/api/payments";
import { currencyFormatter } from "src/utils/common";
import { formatDateTime } from "src/utils/date-time";
import { OrderPaymentsResponse } from "src/types/salePayment.types";
import CheckoutPaymentModal from "./CheckoutPaymentModal";
import PaymentLinkModal from "./PaymentLinkModal";
import {
  LINK_STATUS,
  ORDER_PAYMENT_STATUS,
  REFUND_STATUS,
  paymentMethodLabel,
  timeUntil,
} from "./paymentLabels";

interface OrderPaymentPanelProps {
  order: any;
  canCollect: boolean;
  // Open "Pay now" straight away — used right after creating an order with
  // the Online payment mode.
  autoOpenCollect?: boolean;
  onPaymentsChanged: () => void;
}

const OrderPaymentPanel: React.FC<OrderPaymentPanelProps> = ({
  order,
  canCollect,
  autoOpenCollect = false,
  onPaymentsChanged,
}) => {
  const [data, setData] = useState<OrderPaymentsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  // Collect payment — opens on an open link's QR if there is one.
  const [showCheckout, setShowCheckout] = useState(false);
  // An already-open link's QR / share popup.
  const [showLink, setShowLink] = useState(false);

  const load = useCallback(async () => {
    try {
      const res: any = await paymentApis.getOrderPayments(order._id);
      setData(res?.data || null);
    } catch (error: any) {
      toast.error(error.message || "Failed to load payments");
    } finally {
      setLoading(false);
    }
  }, [order._id]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = data?.order;
  const balanceDue = summary?.balanceDue ?? order.balanceDue ?? order.grandTotal;
  const amountPaid = summary?.amountPaid ?? order.amountPaid ?? 0;
  const isCancelled = order.orderStatus === "cancelled";
  const canTakeMoney = canCollect && !isCancelled && balanceDue > 0;

  useEffect(() => {
    if (autoOpenCollect && !loading && canTakeMoney) setShowCheckout(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenCollect, loading]);

  const latestLink = data?.links?.[0] || null;
  const openLink =
    latestLink && ["active", "partially_paid"].includes(latestLink.status)
      ? latestLink
      : null;
  const paidPercent =
    order.grandTotal > 0 ? Math.min(100, (amountPaid / order.grandTotal) * 100) : 0;
  const status = ORDER_PAYMENT_STATUS[summary?.paymentStatus || order.paymentStatus];

  const handleChanged = () => {
    load();
    onPaymentsChanged();
  };

  const refundDue = data?.refundDue || { online: 0, offline: 0 };
  const refunds = data?.refunds || [];
  const [refunding, setRefunding] = useState<"" | "online" | "offline">("");

  // Retries the gateway refund, or records offline money as handed back.
  const runRefund = async (offlineRefunded: boolean) => {
    try {
      setRefunding(offlineRefunded ? "offline" : "online");
      const res: any = await paymentApis.refundOrder(order._id, { offlineRefunded });
      const result = res?.data;
      if (result?.failed?.length) {
        toast.error(`Refund failed: ${result.failed[0].message}`);
      } else if (offlineRefunded) {
        toast.success(`${currencyFormatter(result?.offlineRefunded || 0)} marked as returned`);
      } else {
        toast.success(`${currencyFormatter(result?.onlineRefunded || 0)} refund initiated via Razorpay`);
      }
      handleChanged();
    } catch (error: any) {
      toast.error(error.message || "Refund failed");
    } finally {
      setRefunding("");
    }
  };

  const copyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy the link");
    }
  };

  return (
    <Card>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Payment</h3>
        {!isCancelled && status && <Badge {...status} />}
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
        {[
          { label: "Subtotal", value: order.subTotal },
          { label: "Tax", value: order.taxAmount },
          { label: "Discount", value: order.discount },
          { label: "Grand total", value: order.grandTotal },
        ].map((row) => (
          <div key={row.label}>
            <div className="text-xs uppercase text-gray-400">{row.label}</div>
            <div className="font-semibold text-gray-900 dark:text-gray-100">
              {currencyFormatter(row.value || 0)}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-xl border border-gray-100 p-4 dark:border-gray-700">
        <div className="flex flex-wrap items-end justify-between gap-3 text-sm">
          <div>
            <div className="text-xs uppercase text-gray-400">Paid</div>
            <div className="text-lg font-semibold text-green-700 dark:text-green-400">
              {currencyFormatter(amountPaid)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase text-gray-400">Balance due</div>
            <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {currencyFormatter(isCancelled ? 0 : balanceDue)}
            </div>
          </div>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
          <div
            className="h-full rounded-full bg-green-500 transition-all"
            style={{ width: `${paidPercent}%` }}
          />
        </div>
        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          Intended payment mode: {order.paymentMode?.toUpperCase() || "-"}
        </div>
      </div>

      {isCancelled && (refundDue.online > 0 || refundDue.offline > 0) && (
        <div className="mt-4 space-y-2 rounded-lg bg-amber-50 px-3 py-3 text-sm text-amber-900 dark:bg-amber-900/30 dark:text-amber-100">
          <div className="flex items-center gap-2 font-semibold">
            <AlertTriangle size={16} className="shrink-0" />
            Refund due: {currencyFormatter(refundDue.online + refundDue.offline)}
          </div>
          {refundDue.online > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                {currencyFormatter(refundDue.online)} paid online — the Razorpay refund didn't
                go through.
              </span>
              {canCollect && (
                <Button
                  size="sm"
                  onClick={() => runRefund(false)}
                  isLoading={refunding === "online"}
                  disabled={refunding !== ""}
                >
                  Retry refund
                </Button>
              )}
            </div>
          )}
          {refundDue.offline > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                {currencyFormatter(refundDue.offline)} collected offline — return it to the
                customer, then mark it here.
              </span>
              {canCollect && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => runRefund(true)}
                  isLoading={refunding === "offline"}
                  disabled={refunding !== ""}
                >
                  Mark {currencyFormatter(refundDue.offline)} returned
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {latestLink && !isCancelled && balanceDue > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-gray-50 px-4 py-3 text-sm dark:bg-gray-900/50">
          <div className="flex flex-wrap items-center gap-2">
            <Badge {...LINK_STATUS[latestLink.status]} />
            <span className="text-gray-700 dark:text-gray-300">
              {currencyFormatter(latestLink.amount)}
              {openLink
                ? ` · expires ${timeUntil(latestLink.expiresAt)}`
                : ` · ${formatDateTime(latestLink.expiresAt, "DD MMM, hh:mm A")}`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {openLink && (
              <Button size="sm" variant="ghost" onClick={() => copyLink(openLink.shortUrl)}>
                <Copy size={14} className="mr-1" />
                Copy
              </Button>
            )}
            {canTakeMoney && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => (openLink ? setShowLink(true) : setShowCheckout(true))}
              >
                {openLink ? (
                  "Show QR / share"
                ) : (
                  <>
                    <RefreshCw size={14} className="mr-1" />
                    Regenerate link
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      )}

      {canTakeMoney && (
        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={() => setShowCheckout(true)}>
            <IndianRupee size={16} className="mr-1.5" />
            Collect payment
          </Button>
        </div>
      )}

      <div className="mt-6">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
          <Wallet size={15} />
          Payment history
        </div>
        <TableV2
          loading={loading}
          skeletonRows={2}
          columns={[
            {
              key: "paidAt",
              header: "Date",
              accessor: ({ row }) => formatDateTime(row.paidAt, "DD MMM YYYY, hh:mm A"),
            },
            { key: "paymentId", header: "Payment ID" },
            {
              key: "amount",
              header: "Amount",
              align: "right",
              accessor: ({ row }) =>
                row.refundedAmount > 0 ? (
                  <div className="text-right">
                    <div>{currencyFormatter(row.amount)}</div>
                    <div className="text-xs text-red-600 dark:text-red-400">
                      −{currencyFormatter(row.refundedAmount)} refunded
                    </div>
                  </div>
                ) : (
                  currencyFormatter(row.amount)
                ),
            },
            {
              key: "method",
              header: "Method",
              accessor: ({ row }) => paymentMethodLabel(row),
            },
            {
              key: "source",
              header: "Source",
              accessor: ({ row }) =>
                row.source === "online" ? "Online" : "Offline",
            },
            {
              key: "reference",
              header: "Reference",
              accessor: ({ row }) => (
                <span className="font-mono text-xs">
                  {row.providerPaymentId || row.referenceNo || "-"}
                </span>
              ),
            },
            {
              key: "recordedBy",
              header: "Recorded by",
              accessor: ({ row }) =>
                row.source === "online"
                  ? "Gateway"
                  : typeof row.recordedBy === "object"
                  ? `${row.recordedBy?.firstName || ""} ${row.recordedBy?.lastName || ""}`.trim() || "-"
                  : "-",
            },
          ]}
          data={data?.payments || []}
        />
      </div>

      {refunds.length > 0 && (
        <div className="mt-6">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
            <Undo2 size={15} />
            Refunds
          </div>
          <TableV2
            columns={[
              {
                key: "createdAt",
                header: "Date",
                accessor: ({ row }) => formatDateTime(row.createdAt, "DD MMM YYYY, hh:mm A"),
              },
              { key: "refundId", header: "Refund ID" },
              {
                key: "amount",
                header: "Amount",
                align: "right",
                accessor: ({ row }) => currencyFormatter(row.amount),
              },
              {
                key: "source",
                header: "Via",
                accessor: ({ row }) =>
                  row.source === "online" ? "Razorpay" : "Returned by hand",
              },
              {
                key: "status",
                header: "Status",
                accessor: ({ row }) => (
                  <div className="flex flex-col items-start gap-0.5">
                    <Badge {...REFUND_STATUS[row.status]} />
                    {row.status === "failed" && row.failureReason && (
                      <span className="text-xs text-red-600 dark:text-red-400">
                        {row.failureReason}
                      </span>
                    )}
                  </div>
                ),
              },
              {
                key: "reference",
                header: "Reference",
                accessor: ({ row }) => (
                  <span className="font-mono text-xs">{row.providerRefundId || "-"}</span>
                ),
              },
            ]}
            data={refunds}
          />
        </div>
      )}

      {showLink && openLink && (
        <PaymentLinkModal
          open={showLink}
          link={openLink}
          orderCode={order.orderId}
          customer={{
            name: order.customerId?.name,
            mobile: order.customerId?.mobile,
            email: order.customerId?.email,
          }}
          onClose={() => setShowLink(false)}
          onChanged={handleChanged}
          onPaid={(received, summary) => {
            toast.success(
              summary && summary.balanceDue > 0
                ? `${currencyFormatter(received)} received — ${currencyFormatter(summary.balanceDue)} still due`
                : `${currencyFormatter(received)} received — order is fully paid`
            );
            setShowLink(false);
            handleChanged();
          }}
        />
      )}
      {showCheckout && (
        <CheckoutPaymentModal
          open={showCheckout}
          order={{ _id: order._id, orderId: order.orderId, balanceDue }}
          onClose={() => {
            setShowCheckout(false);
            // A payment link may have been sent from inside the modal.
            handleChanged();
          }}
          onChanged={handleChanged}
          onPaid={({ amount, balanceDue: remaining }) => {
            toast.success(
              remaining > 0
                ? `${currencyFormatter(amount)} received — ${currencyFormatter(remaining)} still due`
                : `${currencyFormatter(amount)} received — order is fully paid`
            );
            setShowCheckout(false);
            handleChanged();
          }}
        />
      )}
    </Card>
  );
};

export default OrderPaymentPanel;
